<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { DropdownContainer, DropdownBody } from 'next/dropdown-menu/base';
import DashboardHomeAPI from 'dashboard/api/dashboardHome';
import AgendaModal from './AgendaModal.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedUrl } = useAccount();

const scope = ref('personal');
const personalAvailable = ref(false);
const sharedAvailable = ref(false);
const hasConnection = ref(true);
const meetings = ref([]);
const tasks = ref([]);
const isLoading = ref(false);
const activeFilter = ref('all'); // all | meetings | tasks

const filters = [
  { key: 'all', labelKey: 'ALL' },
  { key: 'meetings', labelKey: 'MEETINGS' },
  { key: 'tasks', labelKey: 'TASKS' },
];

// autoSwitch: if opened on 'personal' but the user only has the shared calendar, flip once.
const fetchAgenda = async (autoSwitch = true) => {
  isLoading.value = true;
  try {
    const { data } = await DashboardHomeAPI.getAgenda(scope.value);
    personalAvailable.value = !!data.personal_available;
    sharedAvailable.value = !!data.shared_available;
    hasConnection.value = !!data.has_connection;
    meetings.value = data.meetings || [];
    tasks.value = data.tasks || [];
    if (
      autoSwitch &&
      scope.value === 'personal' &&
      !personalAvailable.value &&
      sharedAvailable.value
    ) {
      scope.value = 'shared';
      await fetchAgenda(false);
    }
  } catch (error) {
    meetings.value = [];
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
};

const setScope = s => {
  if (scope.value === s) return;
  scope.value = s;
  fetchAgenda(false);
};

const showScopeToggle = computed(
  () => personalAvailable.value && sharedAvailable.value
);

const items = computed(() => {
  let list = [];
  if (activeFilter.value === 'tasks') list = tasks.value;
  else if (activeFilter.value === 'meetings') list = meetings.value;
  else list = [...meetings.value, ...tasks.value];
  return [...list].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at)
  );
});

const dayKey = iso => new Date(iso).toDateString();

const dayLabel = iso => {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString())
    return t('AGENDA_DROPDOWN.TODAY');
  if (d.toDateString() === tomorrow.toDateString())
    return t('AGENDA_DROPDOWN.TOMORROW');
  return d.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
};

const groups = computed(() => {
  const map = new Map();
  items.value.forEach(it => {
    if (!it.starts_at) return;
    const k = dayKey(it.starts_at);
    if (!map.has(k)) map.set(k, { label: dayLabel(it.starts_at), items: [] });
    map.get(k).items.push(it);
  });
  return [...map.values()];
});

const timeLabel = it => {
  if (it.all_day) return t('AGENDA_DROPDOWN.ALL_DAY');
  return new Date(it.starts_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const connectUrl = computed(() =>
  accountScopedUrl('settings/integrations/google_calendar')
);

const onItemClick = it => {
  if (it.kind === 'task') {
    router.push(accountScopedUrl('crm/tasks'));
  } else if (it.kind === 'meeting' && it.html_link) {
    window.open(it.html_link, '_blank', 'noopener');
  }
};

const agendaModalRef = ref(null);
const openFull = () => agendaModalRef.value?.open('month');
</script>

<template>
  <DropdownContainer class="relative">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="size-8 rounded-lg hover:bg-n-stone-50 grid place-content-center text-n-stone-400 hover:text-n-stone-700 transition-colors cursor-pointer"
        :class="{ 'bg-n-brand/10 text-n-brand': isOpen }"
        :title="t('AGENDA_DROPDOWN.TITLE')"
        @click="
          () => {
            toggle();
            if (!isOpen) fetchAgenda();
          }
        "
      >
        <span class="i-lucide-calendar-days size-4" />
      </button>
    </template>

    <DropdownBody
      class="top-full mt-1.5 ltr:right-0 rtl:left-0 z-50 w-[380px] max-w-[calc(100vw-1rem)]"
    >
      <!-- Header + scope toggle -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <h4 class="text-sm font-semibold text-n-slate-12 m-0">
          {{ t('AGENDA_DROPDOWN.TITLE') }}
        </h4>
        <div
          v-if="showScopeToggle"
          class="flex bg-n-alpha-2 rounded-md p-0.5 gap-0.5"
        >
          <button
            v-for="s in ['personal', 'shared']"
            :key="s"
            class="px-2 py-0.5 text-[11px] font-medium rounded transition-colors"
            :class="
              scope === s
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="setScope(s)"
          >
            {{ t(`AGENDA_DROPDOWN.SCOPE_${s.toUpperCase()}`) }}
          </button>
        </div>
      </div>

      <!-- Filter tabs -->
      <div class="flex items-center gap-1 px-4 pt-2.5 pb-1">
        <button
          v-for="f in filters"
          :key="f.key"
          class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors"
          :class="
            activeFilter === f.key
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="activeFilter = f.key"
        >
          {{ t(`AGENDA_DROPDOWN.FILTER.${f.labelKey}`) }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <span class="i-lucide-loader-2 size-5 text-n-slate-10 animate-spin" />
      </div>

      <!-- List -->
      <div v-else-if="items.length" class="overflow-y-auto max-h-[400px]">
        <RouterLink
          v-if="!hasConnection && activeFilter !== 'tasks'"
          :to="connectUrl"
          class="flex items-center gap-2 px-4 py-2 text-[11px] text-n-brand hover:bg-n-brand/5 border-b border-n-weak/50"
        >
          <span class="i-lucide-plug size-3.5" />
          {{ t('AGENDA_DROPDOWN.MEETINGS_HINT') }}
        </RouterLink>
        <div v-for="g in groups" :key="g.label">
          <div
            class="px-4 py-1.5 text-[11px] font-semibold text-n-slate-10 bg-n-alpha-1 sticky top-0"
          >
            {{ g.label }}
          </div>
          <button
            v-for="it in g.items"
            :key="`${it.kind}-${it.id}`"
            class="flex items-start gap-2.5 w-full px-4 py-2.5 text-left transition-colors border-b border-n-weak/50 last:border-0 hover:bg-n-slate-2 cursor-pointer"
            @click="onItemClick(it)"
          >
            <span
              class="mt-0.5 size-6 rounded-md grid place-content-center flex-shrink-0"
              :class="
                it.kind === 'meeting'
                  ? 'bg-n-teal-9/10 text-n-teal-9'
                  : it.overdue
                    ? 'bg-red-50 text-red-500'
                    : 'bg-n-brand/10 text-n-brand'
              "
            >
              <span
                class="size-3.5"
                :class="
                  it.kind === 'meeting'
                    ? 'i-lucide-calendar-clock'
                    : 'i-lucide-circle-check'
                "
              />
            </span>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-n-slate-12 m-0 truncate">
                {{ it.title }}
              </p>
              <span
                class="text-[11px]"
                :class="
                  it.overdue ? 'text-red-500 font-semibold' : 'text-n-slate-9'
                "
              >
                {{ timeLabel(it) }}
              </span>
            </div>
            <span
              v-if="it.kind === 'meeting' && it.meet_link"
              class="i-lucide-video size-3.5 text-n-slate-9 mt-1 flex-shrink-0"
            />
          </button>
        </div>
      </div>

      <!-- Needs connection (no meetings, no tasks) -->
      <div
        v-else-if="!hasConnection"
        class="flex flex-col items-center justify-center py-10 px-4 text-center"
      >
        <span class="i-lucide-calendar-off size-6 text-n-slate-8 mb-2" />
        <p class="text-xs text-n-slate-10 m-0 mb-3">
          {{ t('AGENDA_DROPDOWN.NEEDS_CONNECTION') }}
        </p>
        <RouterLink
          :to="connectUrl"
          class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
        >
          <span class="i-lucide-plug size-3.5" />
          {{ t('AGENDA_DROPDOWN.CONNECT') }}
        </RouterLink>
      </div>

      <!-- Empty -->
      <div v-else class="flex flex-col items-center justify-center py-12 px-4">
        <span class="i-lucide-calendar-check size-6 text-n-slate-8 mb-2" />
        <p class="text-sm text-n-slate-10 m-0">
          {{ t('AGENDA_DROPDOWN.EMPTY') }}
        </p>
      </div>

      <!-- Footer: abrir agenda completa (mês/dia/lista) -->
      <div class="border-t border-n-weak px-4 py-2.5">
        <button
          type="button"
          class="flex items-center gap-1.5 text-xs font-medium text-n-brand hover:underline cursor-pointer"
          @click="openFull"
        >
          <span class="i-lucide-calendar-range size-3.5" />
          {{ t('AGENDA_DROPDOWN.OPEN_FULL') }}
        </button>
      </div>
    </DropdownBody>
  </DropdownContainer>

  <AgendaModal ref="agendaModalRef" />
</template>
